import { useState, type JSX } from "react";

import { DownOutlined } from "@ant-design/icons";
import {
    BaseRHFWidgetProps,
    FieldWithId,
} from "@impero-com/common/components/form/rhfFields/types";
import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { Button, FormLabel, InfoTooltip, Svg } from "@impero-com/ui";
import RemoveIcon from "@impero-com/ui/img/remove.svg";
import { Dropdown, Form, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { SorterResult } from "antd/es/table/interface";
import { cx } from "class-variance-authority";
import { ItemType } from "rc-menu/es/interface";
import {
    Control,
    FieldValues,
    useController,
    useFieldArray,
    useForm,
} from "react-hook-form";
import { z } from "zod";

import { UserOverview, StandardAccessRight } from "api/generated/model";
import { getLocalizedAccessRightDescription } from "components/accessRights/localized";
import { UserTypeahead } from "components/userTypeahead";
import { formatAccessRight } from "utils/accessRights";
import { ResourceAccessRights } from "utils/accessRights/models";

import * as theme from "./theme.css";

export function accessRightLiteralSchema<RAR extends ResourceAccessRights>(
    resource: RAR
) {
    // NOTE: Won't work for cases where accessRights has less than 2 variants.
    const [a, b, ...c] = resource.accessRights;
    return z.literal([a, b, ...c.map((value) => value)]);
}

export function userIdAndRightSchema<RAR extends ResourceAccessRights>(
    resource: RAR
) {
    return z.object({
        userId: z.int().nonnegative(),
        right: accessRightLiteralSchema(resource),
    });
}

export function uniqueUsersRefine<RAR extends ResourceAccessRights>(
    values: FormUserIdAndRight<RAR>[]
) {
    // One user can only have one access right. This is enforced by making sure that the
    // users are not selectable from the typeahead, and is also enforced server side.
    // This validation is just a sanity check, it should not be possible to reach this
    // code in production, therefore we don't need to localize it.
    const uniqueUsers = new Set(values.map((u) => u.userId));
    return values.length === uniqueUsers.size;
}

type UserIdAndRightSchemaType<RAR extends ResourceAccessRights> = ReturnType<
    typeof userIdAndRightSchema<RAR>
>;
type FormUserIdAndRight<RAR extends ResourceAccessRights> = z.infer<
    UserIdAndRightSchemaType<RAR>
>;

export function accessRightSchema<RAR extends ResourceAccessRights>(
    resource: RAR
) {
    // eslint-disable-next-line zod/require-error-message
    return z
        .array(userIdAndRightSchema(resource))
        .check((ctx) => {
            if (!resource.requiresAdmin) {
                return;
            }
            // At least one admin is required
            if (ctx.value.findIndex((v) => v.right === "Admin") === -1) {
                ctx.issues.push({
                    code: "custom",
                    message: t("AccessRights_User_WithAdminAccess_Validation"),
                    input: ctx.value,
                });
            }
        })
        .refine(uniqueUsersRefine);
}

export function accessRightOptionalAdminSchema<
    RAR extends ResourceAccessRights,
>(resource: RAR) {
    // eslint-disable-next-line zod/require-error-message
    return z.array(userIdAndRightSchema(resource)).refine(uniqueUsersRefine);
}

type AccessRightSchemaType<RAR extends ResourceAccessRights> = ReturnType<
    typeof accessRightSchema<RAR>
>;
export type UserIdAndRights<RAR extends ResourceAccessRights> = z.infer<
    AccessRightSchemaType<RAR>
>;

type InternalName = "_VIRTUAL_INTERNAL_NAME";
interface HasAccessRights<RAR extends ResourceAccessRights> {
    ["_VIRTUAL_INTERNAL_NAME"]: FormUserIdAndRight<RAR>[];
}

interface RHFAccessRightsProps<
    TFieldsValues extends FieldValues,
    RAR extends ResourceAccessRights,
> extends BaseRHFWidgetProps<TFieldsValues, FormUserIdAndRight<RAR>[]> {
    label?: string;
    disabled?: boolean;
    resourceAccessRights: RAR;
    users: UserOverview[];
}

export function RHFAccessRights<
    TFieldsValues extends FieldValues,
    RAR extends ResourceAccessRights,
>({
    control,
    name,
    label,
    resourceAccessRights,
    users,
    disabled = false,
}: RHFAccessRightsProps<TFieldsValues, RAR>): JSX.Element {
    const internalName = name as InternalName;
    const internalControl = control as FieldValues as Control<
        HasAccessRights<RAR>
    >;

    const { fields, remove, append } = useFieldArray({
        control: internalControl,
        name: internalName,
    });
    const { fieldState, field } = useController({
        control: internalControl,
        name: internalName,
    });
    const [sorterInfo, setSorterInfo] = useState<SorterResult<
        FieldWithId<FormUserIdAndRight<RAR>>
    > | null>({
        order: "ascend",
        columnKey: "accessRight",
    });
    const [lastUserAddedId, setLastUserAddedId] = useState<number | null>(null);
    const [accessRightForNewUser, setAccessRightForNewUser] = useState(
        resourceAccessRights.defaultAccessRight
    );
    // The list of users that have been added through the UserTypeahead
    const [additionalUsers, setAdditionalUsers] = useState<Array<UserOverview>>(
        []
    );

    const tryFindUser = (userId: number): UserOverview | undefined => {
        const maybeUser = users.find((u) => u.id === userId);
        if (maybeUser !== undefined) {
            return maybeUser;
        }
        return additionalUsers.find((u) => u.id === userId);
    };

    const findIndexOfField = (fieldId: string) => {
        return fields.findIndex((field) => field.id === fieldId);
    };

    const compareUsernames = (userIdA: number, userIdB: number): number => {
        const userA = tryFindUser(userIdA);
        const userB = tryFindUser(userIdB);
        return formatUserName(userA).localeCompare(formatUserName(userB));
    };
    const compareAccessRights = (
        rightA: StandardAccessRight,
        rightB: StandardAccessRight
    ): number => {
        return rightA.localeCompare(rightB);
    };

    const onAddAccessRight = (user: UserOverview) => {
        setAdditionalUsers((prevUsers) => [...prevUsers, user]);
        const newUserIdAndRight = {
            userId: user.id,
            right: accessRightForNewUser,
        };
        setLastUserAddedId(user.id);
        append(newUserIdAndRight);
        field.onBlur();
    };

    const onRemoveAccessRight = (fieldId: string) => {
        // Prevents the last user added to blink twice
        setLastUserAddedId(null);
        const index = findIndexOfField(fieldId);
        remove(index);
        field.onBlur();
    };

    const accessRightDropdownItems = (
        onSelectAccessRight: (accessRight: StandardAccessRight) => void
    ) => {
        return resourceAccessRights.accessRights.flatMap(
            (accessRight: StandardAccessRight, rightIndex: number) => {
                const onSelect = () => {
                    // Prevents the last user added to blink twice
                    setLastUserAddedId(null);
                    onSelectAccessRight(accessRight);
                    field.onBlur();
                };
                return accessRightToMenuItems(
                    resourceAccessRights,
                    accessRight,
                    onSelect,
                    rightIndex
                );
            }
        );
    };

    const userHasAccessRight = (userId: number): boolean => {
        return fields.find((u) => u.userId === userId) !== undefined;
    };

    const userColumn = {
        title: t("AccessRights_UserColum"),
        key: "userId",
        render: (_text: string, userIdAndRight: FormUserIdAndRight<RAR>) => (
            <UserColumn
                userIdAndRight={userIdAndRight}
                tryFindUser={tryFindUser}
            />
        ),
        className: theme.header,
        sorter: (
            a: FieldWithId<FormUserIdAndRight<RAR>>,
            b: FieldWithId<FormUserIdAndRight<RAR>>
        ) =>
            compareUsernames(a.userId, b.userId) ||
            compareAccessRights(a.right, b.right),
        sortOrder: sorterInfo?.columnKey === "userId" ? sorterInfo.order : null,
        width: "80%",
    };

    const accessRightsColumn = {
        title: t("AccessRights_AccessRightsColumn"),
        key: "accessRight",
        render: (
            _text: string,
            renderField: FieldWithId<FormUserIdAndRight<RAR>>
        ) => (
            <AccessRightColumn
                internalName={internalName}
                control={internalControl}
                disabled={disabled}
                fieldIndex={findIndexOfField(renderField.id)}
                resourceAccessRights={resourceAccessRights}
                accessRightDropdownItems={accessRightDropdownItems}
            />
        ),
        className: theme.accessRightsHeader,
        filters: resourceAccessRights.accessRights.map((accessRight) => ({
            text: formatAccessRight(accessRight),
            value: accessRight,
        })),
        filterMultiple: false,
        onFilter: (
            value: string,
            record: FieldWithId<FormUserIdAndRight<RAR>>
        ) => record.right.indexOf(value) === 0,
        sorter: (
            a: FieldWithId<FormUserIdAndRight<RAR>>,
            b: FieldWithId<FormUserIdAndRight<RAR>>
        ) =>
            compareAccessRights(a.right, b.right) ||
            compareUsernames(a.userId, b.userId),
        width: "15%",
        sortOrder:
            sorterInfo?.columnKey === "accessRight" ? sorterInfo.order : null,
    };

    const deleteColumn = {
        width: "5%",
        render: (
            _text: string,
            userAndRight: FieldWithId<FormUserIdAndRight<RAR>>
        ) => (
            <DeleteColumn
                onDelete={() => {
                    onRemoveAccessRight(userAndRight.id);
                }}
                userAndRight={userAndRight}
            />
        ),
    };

    const columns: ColumnsType<FieldWithId<FormUserIdAndRight<RAR>>> = disabled
        ? [userColumn, accessRightsColumn]
        : [userColumn, accessRightsColumn, deleteColumn];

    const footerToAddUser = () => {
        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                    <UserTypeahead
                        id="access-rights-table"
                        value={undefined}
                        onSelect={onAddAccessRight}
                        placeholder={t("AccessRights_AddNew")}
                        filter={(user) => !userHasAccessRight(user.id)}
                    />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                    <Dropdown
                        menu={{
                            items: accessRightDropdownItems(
                                setAccessRightForNewUser
                            ),
                        }}
                        trigger={["click"]}
                        placement="bottom"
                    >
                        <a className="ant-dropdown-link" href="#">
                            {formatAccessRight(accessRightForNewUser)}{" "}
                            <DownOutlined />
                        </a>
                    </Dropdown>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
        );
    };

    const tableSummary = disabled ? undefined : footerToAddUser;

    const maybeError =
        fieldState.error?.message ?? fieldState.error?.root?.message;
    const fieldErrors =
        maybeError !== undefined
            ? [<p className={theme.errorText}>{maybeError}</p>]
            : [];
    const classes = [maybeError !== undefined ? ERRORED_CLASSNAME : undefined];
    return (
        <div className={cx(classes)}>
            {label && (
                <FormLabel disabled={disabled} showPencil={false}>
                    {label}
                </FormLabel>
            )}
            <Table
                // eslint-disable-next-line react-hooks/refs
                ref={field.ref}
                summary={tableSummary}
                dataSource={fields}
                columns={columns}
                rowKey={(field) => field.id}
                pagination={false}
                locale={{
                    filterConfirm: t("AccessRights_Filter_Ok"),
                    filterReset: t("AccessRights_Filter_Reset"),
                }}
                rowClassName={(
                    userAndRights: FieldWithId<FormUserIdAndRight<RAR>>
                ) =>
                    userAndRights.userId === lastUserAddedId
                        ? theme.recentlyAddedUser
                        : ""
                }
                onChange={(_pagination, _filters, sorter) => {
                    // Prevents the last user added to blink twice
                    setLastUserAddedId(null);
                    if (Array.isArray(sorter)) {
                        throw new Error("Unexpected type of sorter Array");
                    }
                    setSorterInfo(sorter);
                }}
                bordered
            />
            <Form.ErrorList errors={fieldErrors} />
        </div>
    );
}

interface UserColumnProps<RAR extends ResourceAccessRights> {
    userIdAndRight: FormUserIdAndRight<RAR>;
    tryFindUser: (userId: number) => UserOverview | undefined;
}

function UserColumn<RAR extends ResourceAccessRights>({
    userIdAndRight,
    tryFindUser,
}: UserColumnProps<RAR>) {
    const maybeUser = tryFindUser(userIdAndRight.userId);
    if (maybeUser === undefined) {
        // The user was not found.
        throw new Error("User not found for ID " + userIdAndRight.userId);
    }
    const userName = formatUserName(maybeUser);
    const userEmail = maybeUser.email;

    return (
        <>
            <div className={theme.user}>{userName}</div>
            <div className={theme.email}>{userEmail}</div>
        </>
    );
}

interface AccessRightColumnProps<RAR extends ResourceAccessRights> {
    control: Control<HasAccessRights<RAR>>;
    internalName: InternalName;
    fieldIndex: number;
    resourceAccessRights: RAR;
    disabled: boolean;
    accessRightDropdownItems: (
        update: (accessRight: StandardAccessRight) => void
    ) => ItemType[];
}

function AccessRightColumn<RAR extends ResourceAccessRights>({
    control,
    fieldIndex,
    resourceAccessRights,
    disabled,
    accessRightDropdownItems,
    internalName,
}: AccessRightColumnProps<RAR>) {
    const {
        field,
        fieldState: { error },
    } = useController({ control, name: `${internalName}.${fieldIndex}.right` });
    const { trigger } = useForm();
    const localizedAccessRight = formatAccessRight(field.value);

    const handleAccessRightChange = (newRight: StandardAccessRight) => {
        field.onChange(newRight);
        trigger(`${internalName}.${fieldIndex}.right`);
    };

    if (disabled) {
        // If we're in read-only mode, display the right with a tooltip but no dropdown to change the right
        return (
            <div>
                {formatAccessRight(field.value)}
                <span className={theme.accessRightTooltipContainer}>
                    <InfoTooltip
                        text={getLocalizedAccessRightDescription(
                            resourceAccessRights,
                            field.value
                        )}
                    />
                </span>
            </div>
        );
    }

    const errors =
        error !== undefined
            ? [<p className={theme.errorText}>{t("Validation_Invalid")}</p>]
            : [];
    return (
        <div>
            <Dropdown
                menu={{
                    items: accessRightDropdownItems(handleAccessRightChange),
                }}
                trigger={["click"]}
                placement="bottom"
            >
                <a className="ant-dropdown-link" href="#">
                    {localizedAccessRight} <DownOutlined />
                </a>
            </Dropdown>
            <Form.ErrorList errors={errors} />
        </div>
    );
}

interface DeleteColumnProps<RAR extends ResourceAccessRights> {
    onDelete: () => void;
    userAndRight: FormUserIdAndRight<RAR>;
}

function DeleteColumn<RAR extends ResourceAccessRights>({
    userAndRight,
    onDelete,
}: DeleteColumnProps<RAR>) {
    return (
        <div className={theme.deleteColumn}>
            <Button
                id={"remove-button-" + userAndRight.userId.toString()}
                variant="link"
                className={theme.removeButton}
                onClick={onDelete}
            >
                <Svg className={theme.removeIcon} src={RemoveIcon} />
            </Button>
        </div>
    );
}

function accessRightToMenuItems(
    resourceAccessRights: ResourceAccessRights,
    accessRight: StandardAccessRight,
    onSelect: () => void,
    rightIndex: number
): ItemType[] {
    const menuItemLabel = (
        <div onClick={onSelect}>
            <div className={theme.menuItemTitle}>
                {formatAccessRight(accessRight)}
            </div>
            <div className={theme.menuItemDescription}>
                {getLocalizedAccessRightDescription(
                    resourceAccessRights,
                    accessRight
                )}
            </div>
        </div>
    );

    const menuItem = {
        key: accessRight,
        label: menuItemLabel,
        className: theme.menuItem,
    };

    if (rightIndex > 0) {
        const dividerItem = {
            type: "divider" as const,
            key: rightIndex.toString(),
            className: theme.menuItemDivider,
        };
        return [dividerItem, menuItem];
    } else {
        return [menuItem];
    }
}
